<script lang="ts" setup>
import type { AdminNavGroup, AdminNavItem } from '~kui/types/adminNav';

import { Icon } from '~ds/components/ui/icon';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '~ds/components/ui/sidebar';

interface Props {
  group: AdminNavGroup;
  /** The item the current route resolved to, picked across the whole nav by the page shell. */
  activeItem?: AdminNavItem | null;
}

const props = withDefaults(defineProps<Props>(), { activeItem: null });

const isActive = (item: AdminNavItem): boolean => item === props.activeItem;

// The content domain's brand-lime active treatment, layered over the DS
// sidebar's neutral accent via the `class` prop (twMerge lets the consumer win).
// `bg-secondary` is the toolkit lime (#C4FF57) — the primitive itself stays
// brand-neutral so other domains keep the default accent.
const activeClass = 'data-[active=true]:bg-secondary data-[active=true]:text-secondary-foreground data-[active=true]:font-semibold data-[active=true]:hover:bg-secondary';

// A #nav-item-badge sits absolutely at the row's right edge; reserve the space so a
// long label truncates before it reaches the badge (same trick the DS uses for menu
// actions). Sub-items are grouped as `menu-sub-item`, hence the second class.
const badgeGutterClass = 'group-has-data-[sidebar=menu-badge]/menu-item:pr-8';
const subBadgeGutterClass = 'group-has-data-[sidebar=menu-badge]/menu-sub-item:pr-8';

// A locked destination stays listed and clickable (the page answers with the locked
// takeover), but its whole row is dimmed so the lock reads at a glance, not just from
// the badge at the edge. Opacity rather than a grey token: the sidebar text is already
// a dark neutral, so a muted token alone is too close to tell apart.
const lockedClass = (item: AdminNavItem): string | undefined =>
  item.isLocked ? 'text-muted-foreground opacity-50' : undefined;
</script>

<template>
  <SidebarGroup>
    <SidebarGroupLabel>{{ group.label }}</SidebarGroupLabel>
    <SidebarGroupContent>
      <template
        v-for="subGroup in group.subGroups"
        :key="subGroup.label"
      >
        <div class="px-2 pb-0.5 pt-2 text-[11px] font-medium text-muted-foreground">
          {{ subGroup.label }}
        </div>
        <SidebarMenuSub>
          <SidebarMenuSubItem
            v-for="item in subGroup.items"
            :key="item.label"
          >
            <SidebarMenuSubButton
              as-child
              :is-active="isActive(item)"
              :class="[activeClass, subBadgeGutterClass, lockedClass(item)]"
            >
              <NuxtLink
                :to="item.to ?? '#'"
                :data-testid="item.testId"
                :aria-current="isActive(item) ? 'page' : undefined"
              >
                <Icon
                  v-if="item.iconPath"
                  :name="item.iconPath"
                  size="sm"
                />
                <span>{{ item.label }}</span>
              </NuxtLink>
            </SidebarMenuSubButton>
            <slot name="nav-item-badge" :item="item" />
          </SidebarMenuSubItem>
        </SidebarMenuSub>
      </template>

      <SidebarMenu v-if="group.items?.length">
        <SidebarMenuItem
          v-for="item in group.items"
          :key="item.label"
        >
          <SidebarMenuButton
            as-child
            :is-active="isActive(item)"
            :class="[activeClass, badgeGutterClass, lockedClass(item)]"
          >
            <NuxtLink
              :to="item.to ?? '#'"
              :data-testid="item.testId"
              :aria-current="isActive(item) ? 'page' : undefined"
              @click="item.onClick"
            >
              <Icon
                v-if="item.iconPath"
                :name="item.iconPath"
                size="sm"
              />
              <span>{{ item.label }}</span>
            </NuxtLink>
          </SidebarMenuButton>
          <slot name="nav-item-badge" :item="item" />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>
</template>
